import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import {
  MuiButtonBase,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { getPageUrl, mapLinkToProps } from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { memo } from 'react';
import useStyles from './RowCard.styles';

const RowCard = ({
  cardImage,
  cardDescription,
  cardHeading,
  cardCategory,
  cardPublicationDate,
  cardReadTime,
  cardAuthor,
  cardColor,
  cardTheme,
  cardLogo,
  buttonLink,
  showCaption = true,
  hideImage,
  hideDescription,
  url = '',
  cardVerticalAlignmentNoImage = 'center',
}: any) => {
  const classes = useStyles({});
  const cardClassName = clsx(
    classes.card,
    classes.bgCard,
    `${cardTheme}-theme`,
    `bg-color-${cardColor}`,
    'gtm-headline-container',
    `v-align${cardVerticalAlignmentNoImage}`,
    'gntc-row-card',
  );
  const page = React.useContext(BrPageContext);
  const cardHref = page && getPageUrl({ url, page });
  const cardLinkProps = mapLinkToProps({ href: cardHref });
  const hasCardUrl = !!url;
  const hasImage = !hideImage && cardImage;
  const hasDescription = !hideDescription && cardDescription;

  return (
    <MuiCard className={cardClassName} elevation={8}>
      {hasImage && (
        <MuiCardMedia className={classes.cardImage}>
          <Image image={cardImage} type="card" roundedCorners={false} />
        </MuiCardMedia>
      )}
      <MuiCardContent
        className={hasImage ? classes.cardContent : clsx(classes.cardContent, classes.notCardImage)}
      >
        {cardLogo && !hasImage && (
          <div className={classes.cardLogo}>
            <Image
              image={{ ...cardLogo, fullWidthImage: false }}
              base="original"
              size="contain"
              position="left"
              roundedCorners={false}
            />
          </div>
        )}
        {cardCategory && (
          <MuiTypography variant="overline" component="div" className={classes.category}>
            {cardCategory.label || cardCategory}
          </MuiTypography>
        )}
        {cardHeading && (
          <MuiTypography
            className={clsx(classes.cardHeading, 'gtm-headline')}
            variant="h6"
            dangerouslySetInnerHTML={{ __html: cardHeading }}
          />
        )}
        {hasDescription && (
          <CaptureClicksRichText
            theme={cardTheme}
            containerProps={{ className: classes.cardDescription }}
            content={cardDescription}
          />
        )}
        <div className={classes.spacer} />
        {buttonLink?.displayText && (
          <MuiCardActions className={classes.cardActions}>
            <Button
              link={buttonLink}
              theme={cardTheme}
              className={clsx(classes.cardActionsLink, 'gtm-featured-content-cta')}
            />
          </MuiCardActions>
        )}

        {!hideDescription &&
          showCaption &&
          (cardReadTime || cardPublicationDate || cardAuthor?.name) && (
            <MuiTypography variant="caption" component="div" className={clsx(classes.caption)}>
              {cardPublicationDate && <span>{cardPublicationDate}</span>}
              {cardPublicationDate && cardReadTime && <span className={classes.divider}>|</span>}
              {cardReadTime && <span>{cardReadTime}</span>}
              {cardAuthor && <p>{cardAuthor?.name}</p>}
            </MuiTypography>
          )}
      </MuiCardContent>
      {hasCardUrl && (
        <MuiButtonBase
          {...cardLinkProps}
          className={clsx(classes.cardLink, 'gtm-featured-content-cta')}
        />
      )}
    </MuiCard>
  );
};

export default memo(RowCard);
